import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Card, CardBody, CardHeader, Button, ButtonGroup,
  Row, Col, Container, Spinner, Form, FormGroup, Label, Input,
  Table, Badge
} from 'reactstrap';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, parseISO, formatISO, parse } from 'date-fns';
import { fr } from 'date-fns/locale';
import { saleActions } from '../../sagas/saleSlice';
import { FaBarcode, FaCalendarAlt, FaDownload, FaFileAlt, FaSearch, FaSyncAlt, FaUser } from 'react-icons/fa';
import { convertNumberToWords, formatPrice } from '../../helpers/utility';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useReactToPrint } from 'react-to-print';
import { clientActions } from '../../sagas/clientSlice';
import Select from "react-select";
import autoTable from 'jspdf-autotable';
import DocumentPagination from '../Common/DocumentPagination';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const VentesStatistiques = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { company } = useSelector(state => state.auth);

  const now = new Date();
  const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const pad = (n) => n.toString().padStart(2, '0');
  const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const formatMonth = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
  const getMonthRange = (monthValue) => {
    const [year, month] = monthValue.split('-').map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    return {
      dateDebut: formatDate(firstDay),
      dateFin: formatDate(lastDay)
    };
  };
  const currentMonth = formatMonth(todayDate);
  const currentMonthRange = getMonthRange(currentMonth);

  const [filters, setFilters] = useState({
    clientId: null,
    chantierId: null,
    month: currentMonth,
    dateDebut: currentMonthRange.dateDebut,
    dateFin: currentMonthRange.dateFin,
    query: ''
  });
  const [searchValue, setSearchValue] = useState('');
  const [pagination, setPagination] = useState({
    pageNumber: 0,
    pageSize: 25
  });

  const generateClientReport = useCallback(() => {
    dispatch(saleActions.generateClientReport({ ...filters, page: 0, size: 25 }));
  }, [dispatch, filters]);

  useEffect(() => {
    dispatch(saleActions.clearClientReport());
    generateClientReport();
  }, [dispatch, generateClientReport]);

  useEffect(() => {
    const debounceId = window.setTimeout(() => {
      setPagination(current => ({ ...current, pageNumber: 0 }));
      setFilters(current => ({
        ...current,
        query: searchValue.trim()
      }));
    }, 450);

    return () => window.clearTimeout(debounceId);
  }, [searchValue]);

  const parseDate = (dateString) => {
    if (!dateString) return new Date();

    if (dateString.includes('-')) {
      return parseISO(dateString);
    }

    return parse(dateString, 'dd/MM/yyyy', new Date());
  };

  // Client report state
  const { clients, loading } = useSelector(state => state.clients);
  const { clientReport, clientReportLoading, clientReportError } = useSelector(state => state.sales);
  const [selectedClient, setSelectedClient] = useState('');
  const clientReportRef = useRef(null);

  const clientOptions = clients?.map(client => ({
    value: client.id,
    label: client.typeClient === 'PARTICULIER'
      ? `${client.nom} ${client.prenom || ''} `
      : client.entreprise,
    chantiers: client.chantiers || []
  })) ?? [];

  const clientOptionsWithAll = [
    { value: null, label: t('common.all', 'Tous') },
    ...clientOptions
  ];

  const chantierOptions = selectedClient?.chantiers?.map(chantier => ({
    value: chantier.id,
    label: `${chantier.code} - ${chantier.nom}`,
    ...chantier
  })) || [];

  const chantierOptionsWithAll = [
    { value: null, label: t('common.all', 'Tous') },
    ...chantierOptions
  ];

  // Effect for fetching data
  useEffect(() => {
    if (clients.length === 0) {
      // Fetch clients for client report
      const fetchClients = async () => {
        dispatch(clientActions.search({
          query: ""
        }));
      };

      fetchClients();
    }
  }, []);

  const submitSearch = (value = searchValue) => {
    setPagination(current => ({ ...current, pageNumber: 0 }));
    setFilters(current => ({
      ...current,
      query: value.trim()
    }));
  };

  const handleMonthChange = (monthValue) => {
    if (!monthValue) {
      updateFilters({ month: '' });
      return;
    }

    const monthRange = getMonthRange(monthValue);
    updateFilters({
      month: monthValue,
      dateDebut: monthRange.dateDebut,
      dateFin: monthRange.dateFin
    });
  };


  const exportClientReportToPDF = () => {
    if (!clientReport) return;

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4"
    });

    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 10;
    const footerHeight = 15;

    // ---- GREEN & WHITE THEME (SAME AS SUPPLIER) ----
    const primaryGreen = { r: 46, g: 204, b: 113 };
    const altRow = { r: 232, g: 248, b: 245 };
    const white = { r: 255, g: 255, b: 255 };
    const black = { r: 0, g: 0, b: 0 };
    const negativeRed = { r: 231, g: 76, b: 60 };

    // ---------------- HEADER ----------------
    const addPageHeader = () => {
      doc.setFillColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.rect(0, 0, pageWidth, 30, "F");
      doc.rect(0, 25, pageWidth, 5, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.text("VENTES PAR ARTICLE", pageWidth / 2, 15, { align: "center" });

      const from = format(parseDate(clientReport.period.from), "dd/MM/yyyy", { locale: fr });
      const to = format(parseDate(clientReport.period.to), "dd/MM/yyyy", { locale: fr });

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.text(`Période: du ${from} au ${to}`, pageWidth / 2, 22, { align: "center" });
    };

    // ---------------- FOOTER ----------------
    const addPageFooter = () => {
      const y = pageHeight - footerHeight;

      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y, pageWidth - margin, y);

      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(`Document généré le: ${clientReport.generationDate}`, pageWidth - margin, y + 5, { align: "right" });
      doc.text(company.name, margin, y + 5);
      doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageWidth / 2, y + 5, { align: "center" });
    };

    // ---- FIRST PAGE ----
    addPageHeader();

    // ---- COMPANY INFO ----
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(black.r, black.g, black.b);
    doc.text(company.name, margin, 40);

    doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
    doc.line(margin, 42, margin + 60, 42);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`Adresse: ${company.address}`, margin, 47);
    doc.text(`Tél: ${company.phone1} | Email: ${company.email}`, margin, 52);
    doc.text(`RC: ${company.rc} | IF: ${company.identificationFiscale} | ICE: ${company.ice}`, margin, 57);

    // ---- CLIENT BOX ----
    if (clientReport.client) {
      doc.setFillColor(white.r, white.g, white.b);
      doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.roundedRect(pageWidth - margin - 80, 35, 80, 30, 3, 3, "FD");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.text("CLIENT", pageWidth - margin - 75, 42);

      doc.line(pageWidth - margin - 75, 44, pageWidth - margin - 15, 44);

      doc.setFont("helvetica", "normal");
      doc.setTextColor(black.r, black.g, black.b);
      doc.text(`Nom: ${clientReport.client.name}`, pageWidth - margin - 75, 50);
      doc.text(`Téléphone: ${clientReport.client.phone}`, pageWidth - margin - 75, 55);
      doc.text(`Code: ${clientReport.client.code}`, pageWidth - margin - 75, 60);
    }

    // ---------------- TABLE ----------------
    const headers = ["Article", "Code-barres", "Quantité vendue", "Unité", "Total ventes"];
    const rows = (clientReport.articleSales || []).map(article => [
      article.designation, article.barcode || '', String(article.quantity ?? 0), article.unit || '', formatPrice(article.lineTotal, false)
    ]);

    autoTable(doc, {
      startY: 70,
      head: [headers],
      body: rows,
      theme: "grid",

      headStyles: {
        fillColor: [primaryGreen.r, primaryGreen.g, primaryGreen.b],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
        lineWidth: 0.3,
        lineColor: [0, 0, 0]
      },

      alternateRowStyles: {
        fillColor: [altRow.r, altRow.g, altRow.b]
      },

      styles: {
        cellPadding: 3,
        fontSize: 9,
        textColor: [0, 0, 0],
        lineWidth: 0.25,
        lineColor: [150, 150, 150]
      },

      margin: { top: margin, bottom: footerHeight },
      didDrawPage: addPageFooter
    });

    if (clientReport.summary) {
      // ---------------- SUMMARY ----------------
      const s = clientReport.summary;
      const summaryY = doc.lastAutoTable.finalY + 15;

      doc.setDrawColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(margin, summaryY, 90, 40, 3, 3, "FD");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      doc.text("RÉSUMÉ", margin + 5, summaryY + 7);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(black.r, black.g, black.b);

      doc.text("Montant Total Consommé:", margin + 5, summaryY + 15);
      doc.text(formatPrice(s.totalConsome, false), margin + 85, summaryY + 15, { align: "right" });

      doc.text("Solde Avant:", margin + 5, summaryY + 22);
      doc.text(formatPrice(s.soldeAvant, false), margin + 85, summaryY + 22, { align: "right" });

      doc.text("Avance:", margin + 5, summaryY + 29);
      doc.text(formatPrice(s.avance, false), margin + 85, summaryY + 29, { align: "right" });

      doc.text("Avoir:", margin + 5, summaryY + 36);
      doc.text(formatPrice(s.avoir, false), margin + 85, summaryY + 36, { align: "right" });

      // ---- NET À PAYER ----
      const net = parseFloat(s.netApaye);
      const netY = summaryY + 55;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);

      if (net < 0) {
        doc.setTextColor(negativeRed.r, negativeRed.g, negativeRed.b);
      } else {
        doc.setTextColor(primaryGreen.r, primaryGreen.g, primaryGreen.b);
      }

      doc.text("Net À Payer :", margin, netY);
      doc.text(formatPrice(net, false), margin + 60, netY);
    }


    // ---------------- OPEN IN NEW TAB ----------------
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };


  const articleSales = useMemo(() => clientReport?.articleSales || [], [clientReport]);
  const totalElements = articleSales.length;
  const currentPageSize = pagination.pageSize;
  const totalPages = Math.ceil(totalElements / currentPageSize);
  const currentPageNumber = Math.min(pagination.pageNumber, Math.max(0, totalPages - 1));
  const reportRows = useMemo(() => articleSales.slice(currentPageNumber * currentPageSize, (currentPageNumber + 1) * currentPageSize), [articleSales, currentPageNumber, currentPageSize]);
  const pageQuantityTotal = reportRows.reduce((sum, row) => sum + Number(row.quantity || 0), 0);
  const pageAmountTotal = reportRows.reduce((sum, row) => sum + Number(row.lineTotal || 0), 0);
  const fullQuantityTotal = articleSales.reduce((sum, row) => sum + Number(row.quantity || 0), 0);
  const fullAmountTotal = articleSales.reduce((sum, row) => sum + Number(row.lineTotal || 0), 0);
  const pageStart = totalElements === 0 ? 0 : currentPageNumber * currentPageSize + 1;
  const pageEnd = Math.min((currentPageNumber + 1) * currentPageSize, totalElements);

  const updateFilters = (nextValues) => {
    setPagination(current => ({ ...current, pageNumber: 0 }));
    setFilters(current => ({ ...current, ...nextValues }));
  };

  const handlePageChange = (pageNumber) => {
    setPagination(current => ({ ...current, pageNumber }));
  };

  const handlePageSizeChange = (event) => {
    setPagination({
      pageNumber: 0,
      pageSize: Number(event.target.value)
    });
  };


  return (
    <Container fluid className="page-content ventes-statistiques-page">
      <Card className="shadow-sm mb-4 border-0 ventes-shell">
        <CardHeader className="bg-white border-0 pb-2">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1 ventes-heading-row">
                <span className="ventes-title-icon">
                  <FaFileAlt />
                </span>
                <h4 className="mb-0 fw-bold">Détails des ventes</h4>
              </div>
              <div className="text-muted small">
                Quantités vendues par article sur la période choisie. Recherche par nom ou code-barres.
              </div>
            </div>

            <div className="d-flex gap-2">
              <Button
                color="primary"
                onClick={() => generateClientReport()}
                disabled={clientReportLoading}
              >
                {clientReportLoading ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Génération...
                  </>
                ) : (
                  <>
                    <FaSyncAlt className="me-1" />
                    Générer
                  </>
                )}
              </Button>

              {clientReport && (
                <Button
                  color="secondary"
                  size="sm"
                  onClick={exportClientReportToPDF}
                >
                  <FaDownload className="me-1" />
                  Export PDF
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardBody className="pt-2">
          <Row className="g-2 align-items-end mb-3 ventes-filter-bar">
            <Col xl={3} lg={4} md={6} xs={12}>
              <FormGroup className="mb-0">
                <Label className="mb-1 fw-semibold">Article / code-barres</Label>
                <div className="input-group">
                  <span className="input-group-text bg-white">
                    <FaSearch />
                  </span>
                  <Input
                    type="text"
                    placeholder="Libelle article ou scan code-barres"
                    value={searchValue}
                    onChange={e => {
                      setSearchValue(e.target.value);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        submitSearch(e.target.value);
                      }
                    }}
                  />
                  <Button color="light" className="border" onClick={() => submitSearch()}>
                    <FaBarcode />
                  </Button>
                </div>
              </FormGroup>
            </Col>
            <Col xl={3} lg={4} md={6}>
              <FormGroup className="mb-0">
                <Label for="clientSelect" className="form-label">
                  <FaUser className="me-1" /> Client
                </Label>
                <Select
                  id="clientId"
                  options={clientOptionsWithAll}
                  value={clientOptionsWithAll.find(option => option.value === filters.clientId)}
                  onChange={(selectedOption) => {
                    updateFilters({ clientId: selectedOption ? selectedOption.value : null, chantierId: null });
                    setSelectedClient(selectedOption);
                  }}
                  placeholder={t('bonLivraison.selectClient')}
                  isSearchable
                  menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 })
                  }}
                />
              </FormGroup>
            </Col>
            <Col xl={2} lg={4} md={6}>
              <FormGroup className="mb-0">
                <Label for="monthFilter" className="form-label">
                  <FaCalendarAlt className="me-1" /> Mois
                </Label>
                <Input
                  type="month"
                  id="monthFilter"
                  value={filters.month}
                  onChange={e => handleMonthChange(e.target.value)}
                />
              </FormGroup>
            </Col>

            <Col xl={2} lg={4} md={6}>
              <FormGroup className="mb-0">
                <Label for="startDate" className="form-label">
                  <FaCalendarAlt className="me-1" /> Date de début
                </Label>
                <Input
                  type="date"
                  id="dateDebut"
                  value={filters.dateDebut}
                  onChange={e => updateFilters({ month: '', dateDebut: e.target.value })}
                />
              </FormGroup>
            </Col>
            <Col xl={2} lg={4} md={6}>
              <FormGroup className="mb-0">
                <Label for="endDate" className="form-label">
                  <FaCalendarAlt className="me-1" /> Date de fin
                </Label>
                <Input
                  type="date"
                  id="dateFin"
                  value={filters.dateFin}
                  onChange={e => updateFilters({ month: '', dateFin: e.target.value })}
                />
              </FormGroup>
            </Col>

          </Row>

          {/* Client Report Content */}
          <div ref={clientReportRef}>
            {clientReport ? (
              <div className="client-report p-2 p-lg-3 bg-white animate__animated animate__fadeIn">
                {/* <div className="report-header mb-4 pb-2 border-bottom">
                  <div className="text-center mb-3">
                    <h4 className="text-primary mb-1 fw-bold letter-spacing-1">RAPPORT VENTE CLIENT</h4>
                    <p className="text-muted small mb-0">
                      <FaCalendarAlt className="me-1" />
                      Période: <span className="fw-semibold">du {format(parseDate(clientReport.period.from), 'dd/MM/yyyy', { locale: fr })} au {format(parseDate(clientReport.period.to), 'dd/MM/yyyy', { locale: fr })}</span>
                    </p>
                  </div>

                  <Row className="mb-3 g-4">
                    <Col md={6}>
                      <div className="company-info bg-light p-3 rounded-3 h-100">
                        <h5 className="mb-2 text-dark fw-bold"><FaFileAlt className="me-2 text-primary" />{company.name}</h5>
                        <div className="small text-secondary">
                          <div><span className="fw-semibold">Adresse:</span> {company.address}</div>
                          <div><span className="fw-semibold">Tél:</span> {company.phone1} <span className="mx-1">|</span> <span className="fw-semibold">Email:</span> {company.email}</div>
                          <div><span className="fw-semibold">RC:</span> {company.rc} <span className="mx-1">|</span> <span className="fw-semibold">IF:</span> {company.identificationFiscale} <span className="mx-1">|</span> <span className="fw-semibold">ICE:</span> {company.ice}</div>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="client-info bg-white border border-2 border-primary p-3 rounded-3 h-100">
                        <h6 className="mb-2 text-primary fw-bold"><FaUser className="me-2" />Informations Client</h6>
                        <div className="small">
                          <div className="mb-1"><strong>Client:</strong> {clientReport.client.name}</div>
                          <div className="mb-1"><strong>Téléphone:</strong> {clientReport.client.phone}</div>
                          <div className="mb-0"><strong>Code client:</strong> {clientReport.client.code}</div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div> */}

                <Row className="g-2 mb-3">
                  <Col lg={3} sm={6}>
                    <div className="ventes-metric">
                      <span>Articles vendus</span>
                      <strong>{totalElements}</strong>
                    </div>
                  </Col>
                  <Col lg={3} sm={6}>
                    <div className="ventes-metric">
                      <span>Quantite totale</span>
                      <strong>{Number(fullQuantityTotal || 0).toFixed(2)}</strong>
                    </div>
                  </Col>
                  <Col lg={3} sm={6}>
                    <div className="ventes-metric">
                      <span>Total ventes</span>
                      <strong>{formatPrice(fullAmountTotal || 0, false)}</strong>
                    </div>
                  </Col>
                  <Col lg={3} sm={6}>
                    <div className="ventes-metric">
                      <span>Page affichee</span>
                      <strong>{totalPages ? `${currentPageNumber + 1}/${totalPages}` : '0/0'}</strong>
                    </div>
                  </Col>
                </Row>

                <div className="report-body mb-3">
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                    <div>
                      <h6 className="mb-1 fw-bold">Total vendu par article</h6>
                      <div className="text-muted small">
                        Affichage {pageStart} a {pageEnd} sur {totalElements} resultats.
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Badge color="light" className="text-dark border">Totaux de toute la période</Badge>
                      <Input
                        type="select"
                        bsSize="sm"
                        value={pagination.pageSize}
                        onChange={handlePageSizeChange}
                        style={{ width: 96 }}
                      >
                        {PAGE_SIZE_OPTIONS.map(size => (
                          <option key={size} value={size}>{size}/page</option>
                        ))}
                      </Input>
                    </div>
                  </div>
                  <div className="table-responsive ventes-table-wrap">

                    <Table bordered hover size="sm" className="mb-0 align-middle">
                      <thead className="table-primary">
                        <tr>
                          <th className="text-center">Désignation</th>
                          <th className="text-center">Quantité vendue</th>
                          <th className="text-end">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportRows.length === 0 && <tr><td colSpan={3} className="text-center py-4">Aucun article vendu sur cette période.</td></tr>}
                        {reportRows.map((note, index) => (
                          <tr key={note.id} className={index % 2 === 0 ? "bg-light" : ""}>
                            <td>
                              <div className="fw-semibold">{note.designation}</div>
                              <div className="text-muted small">
                                <FaBarcode className="me-1" />
                                {note.barcode || 'Sans code-barres'}
                              </div>
                            </td>
                            <td className="text-center">
                              <div className="fw-semibold">{note.quantity}</div>
                              <div className="text-muted small">{note.unit}</div>
                            </td>
                            <td className="text-end fw-bold">{formatPrice(note.lineTotal, false)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <td colSpan="1" className="text-end fw-bold">Total page:</td>
                          <td className="text-center fw-bold text-primary">
                            {Number(pageQuantityTotal || 0).toFixed(2)}
                          </td>
                          <td className="text-end fw-bold text-primary">{formatPrice(pageAmountTotal, false)}</td>
                        </tr>
                        <tr>
                          <td colSpan="1" className="text-end fw-bold">Total resultats:</td>
                          <td className="text-center fw-bold text-success">{Number(fullQuantityTotal || 0).toFixed(2)}</td>
                          <td className="text-end fw-bold text-success">{formatPrice(fullAmountTotal || 0, false)}</td>
                        </tr>
                      </tfoot>
                    </Table>

                  </div>
                  <DocumentPagination
                    pageNumber={currentPageNumber}
                    totalPages={totalPages}
                    totalElements={totalElements}
                    pageSize={currentPageSize}
                    onPageChange={handlePageChange}
                  />
                </div>


                {clientReport && clientReport.summary && <div className="report-summary mb-4">
                  <Row className="g-4">
                    <Col md={6}>
                      <div className="mb-3 bg-light p-3 rounded-3 border border-1 border-secondary">
                        <h6 className="border-bottom pb-2 mb-3 text-secondary fw-bold">Résumé</h6>
                        <Table borderless size="sm" className="summary-table">
                          <tbody>
                            <tr>
                              <td className="fw-bold">Montant Total Consommé</td>
                              <td className="text-end">{formatPrice(clientReport.summary.totalConsome || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Solde Avant le {format(parseDate(clientReport.period.from), 'dd/MM/yyyy', { locale: fr })}</td>
                              <td className="text-end">{formatPrice(clientReport.summary.soldeAvant || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Avance</td>
                              <td className="text-end">{formatPrice(clientReport.summary.avance || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Règlements normaux (hors avances)</td>
                              <td className="text-end">{formatPrice(clientReport.summary.reglements || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Avoir</td>
                              <td className="text-end">{formatPrice(clientReport.summary.avoir || 0, false)}</td>
                            </tr>
                            <tr className="table-light">
                              <td className="fw-bold">Net A Payer</td>
                              {
                                clientReport.summary.netApaye < 0 ? (
                                  <td className="text-end fw-bold text-danger">{formatPrice(clientReport.summary.netApaye, false)}</td>
                                ) : (
                                  <td className="text-end fw-bold text-success">{formatPrice(clientReport.summary.netApaye, false)}</td>
                                )
                              }
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="amount-in-words p-4 bg-white rounded-3 border border-1 border-primary">
                        <p className="mb-1 fw-bold text-primary">Arrêté le présent document à la somme de:</p>
                        {
                          clientReport.summary.netApaye < 0 ?
                            < p className="fst-italic text-danger fs-5">
                              {`Moins ${convertNumberToWords(Math.abs(clientReport.summary.netApaye))} `}
                              Dirhams
                            </p> :
                            <p className="fst-italic text-success fs-5">
                              {`${convertNumberToWords(Math.abs(clientReport.summary.netApaye))} `}
                              Dirhams
                            </p>
                        }
                        <p className="mt-3 small text-muted">
                          <FaCalendarAlt className="me-1" />
                          NB: Montant Total Consommé sur la période du <span className="fw-semibold">{format(parseDate(clientReport.period.from), 'dd/MM/yyyy', { locale: fr })} au {format(parseDate(clientReport.period.to), 'dd/MM/yyyy', { locale: fr })}</span>
                        </p>


                      </div>
                    </Col>
                  </Row>
                </div>}

                <div className="report-footer mt-4 pt-3 border-top text-muted small">
                  <div className="d-flex justify-content-between align-items-center">
                    <span><FaCalendarAlt className="me-1" /> Document généré le: <span className="fw-semibold">{clientReport.generationDate}</span></span>
                    <span className="fw-bold">Page 1</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center p-5 bg-light rounded-4 border border-2 border-secondary animate__animated animate__fadeIn">
                {clientReportLoading ? (
                  <div>
                    <Spinner color="primary" />
                    <p className="mt-3">Génération du rapport en cours...</p>
                  </div>
                ) : (
                  <div>
                    <FaFileAlt size={48} className="text-muted mb-3" />
                    <h5 className="fw-bold">Aucun rapport généré</h5>
                    <p className="text-muted">
                      Veuillez sélectionner un client et cliquer sur <span className="fw-semibold">"Générer le rapport"</span> pour afficher les détails des ventes.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      <style>{`
        .ventes-statistiques-page {
          background: #f6f8fb;
        }
        .ventes-shell {
          border-radius: 8px;
        }
        .ventes-title-icon {
          width: 36px;
          height: 36px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          background: #eef5ff;
          color: #0d6efd;
        }
        .ventes-heading-row {
          color: transparent;
          font-size: 0;
        }
        .ventes-heading-row .ventes-title-icon,
        .ventes-heading-row h4 {
          color: #1f2937;
          font-size: 1.5rem;
        }
        .ventes-heading-row .ventes-title-icon {
          color: #0d6efd;
        }
        .ventes-filter-bar {
          padding: 0.65rem 0.75rem;
          margin: 0 0 0.85rem 0;
          background: #f8fafc;
          border: 1px solid #e7edf5;
          border-radius: 8px;
        }
        .ventes-metric {
          height: 100%;
          padding: 0.55rem 0.75rem;
          border: 1px solid #e8eef5;
          border-radius: 8px;
          background: #fbfdff;
        }
        .ventes-metric span {
          display: block;
          color: #6c757d;
          font-size: 0.78rem;
          font-weight: 600;
          text-transform: uppercase;
        }
        .ventes-metric strong {
          display: block;
          margin-top: 0.15rem;
          color: #1f2937;
          font-size: 1.05rem;
        }
        .ventes-table-wrap {
          border: 1px solid #e6ebf1;
          border-radius: 8px;
          overflow: hidden;
        }
        .ventes-table-wrap table thead th {
          background: #eef5ff;
          color: #1f3b57;
          border-bottom: 1px solid #dbe7f5;
          white-space: nowrap;
        }
        .ventes-table-wrap table tbody td {
          vertical-align: middle;
        }
        .client-report {
          font-size: 0.9rem;
        }
        .summary-table td {
          padding: 0.4rem 0.75rem;
        }
        .company-info h5 {
          color: #2c3e50;
        }
        .amount-in-words {
          border-left: 3px solid #3498db;
        }
        @media print {
          .page-content {
            padding: 0 !important;
          }
          .client-report {
            border: none !important;
          }
        }
      `}</style>
    </Container>
  );
};

export default VentesStatistiques;
